import React, { memo, useContext, useState, useEffect } from 'react';
import { useTheme, type Theme, type ThemeProps } from '@emotion/react';
import { useRouter } from 'next/router';
import styled, { type CSSObject } from '@emotion/styled';

import { WWContext, type WWContextValue } from '../WWContext';
import Announcement from './Announcement';
import Header from './Header';
import Nav from './Nav';

import { isWwsWeddingWebsiteV2 } from '../../types/graphql';
import ThemeNextImage from '../NextImage/ThemeNextImage';
import { mobileOnly, desktopOnly } from '../../lib/utils';

interface TopImageProps {
  height: number;
}

const TopFrame = styled('div')<TopImageProps>`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  pointer-events: none;
  height: ${({ height }) => height}px;
`;

const MobileTitleBarOffset = styled('div')`
  height: 56px;
  width: 100%;

  @media (min-width: 768px) {
    display: none;
  }
`;

const TopImageWrap = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TopImage = styled(ThemeNextImage)`
  ${({ theme }: ThemeProps): CSSObject | undefined => theme.components.TopImage}
`;

const MobileHeader = mobileOnly(Header);

const DesktopHeader = desktopOnly(Header);

function TopMatter() {
  const [height, setHeight] = useState(0);

  // calculate bounding box area for topFrame needed for home page screenshot
  useEffect(() => {
    const topImageElem = document.getElementById('top-image');
    const coverPhotoElem = document.getElementById('cover-photo');

    if (coverPhotoElem) {
      let distance = coverPhotoElem.getBoundingClientRect().bottom;

      // if topImageElem is not mounted, manually add 120px to the height
      if (!topImageElem) {
        distance += 120;
      }

      setHeight(distance + 16);
    }
  }, []);

  const theme: Theme = useTheme();

  const { query } = useRouter();

  const { data } = useContext<WWContextValue>(WWContext);

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const { top } = theme.images;

  const { top: topResponsive } = theme.responsiveImages || {};

  const { route = [] } = query || {};

  const isHomePage = !route.length;

  const hero: string | null | undefined = isHomePage
    ? data?.weddingWebsiteResult.coverPhoto?.path
    : undefined;

  const displayHeaderOnHero = (hero && theme.config.headerOnHero) || false;

  return (
    <>
      <TopFrame id="topFrame" height={height} />
      {/* Mobile Top Matter */}
      <MobileTitleBarOffset />

      <Announcement
        announcement={data?.weddingWebsiteResult?.announcement ?? undefined}
      />

      <TopImageWrap>
        <TopImage
          id="top-image"
          src={top || ''}
          mobileSrc={topResponsive?.smUrl || ''}
          alt="Top Theme Image"
          imageCSSObject={theme.components.TopImage}
          loading="eager"
          priority
          isTopImage
          desktopSizes="(min-width: 768px) 100vw, 0vw"
          mobileSizes="(max-width: 767px) 100vw, 0vw"
        />
      </TopImageWrap>

      <MobileHeader heroVersion={false} />

      {!displayHeaderOnHero ? <DesktopHeader heroVersion={false} /> : null}

      <Nav />
    </>
  );
}

export default memo(TopMatter);
